import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics, schemaArrayJSONString } =
    props?.config || {};

  const { googleAnalyticsId } = analytics || {};
  const { mapProvider, googleMapsAPIKey } = maps || {};

  const isBrowser = typeof window !== 'undefined';
  const isMapboxInUse = mapProvider === 'mapbox';
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const hasGA4 = !!googleAnalyticsId && googleAnalyticsId.startsWith('G-');

  const [clientScripts, setClientScripts] = useState([]);

  // ------------------------------------------------------
  // CLIENT-SIDE SCRIPT LOADER (executes ONLY in the browser)
  // ------------------------------------------------------
  useEffect(() => {
    if (!isBrowser) return;

    const scripts = [];

    // --------------------------
    // GA4 INLINE CONFIG (browser only)
    // --------------------------
    if (hasGA4) {
      scripts.push(
        <script
          key="ga4-inline"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${googleAnalyticsId}');
            `,
          }}
        />
      );
    }

    // --------------------------
    // HOTJAR REMOVED 2026-09-07: redundant with Microsoft Clarity (below,
    // moved to public/index.html). Clarity covers session replay + heatmaps
    // with no sampling. Not used. Do not re-add.
    // --------------------------

    // --------------------------
    // MICROSOFT CLARITY — MOVED to public/index.html (2026-09-07).
    // It was here as a React-rendered inline <script>, but browsers do NOT
    // execute inline scripts that React inserts into the DOM after the
    // initial parse, so it never ran (Clarity stuck on "Almost There").
    // Now hardcoded in public/index.html alongside GA4/Meta Pixel, which is
    // the pattern that actually executes. Do not re-add it here.
    // --------------------------

    // --------------------------
    // FACEBOOK PIXEL (browser only)
    // --------------------------
    scripts.push(
      <script
        key="facebook-pixel"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s){
              if(f.fbq)return;
              n=f.fbq=function(){n.callMethod ?
                n.callMethod.apply(n,arguments) : n.queue.push(arguments)};
              if(!f._fbq) f._fbq=n;
              n.push=n; n.loaded=!0; n.version='2.0'; n.queue=[];
              t=b.createElement(e); t.async=!0;
              t.src=v; s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s);
            }(window, document, 'script',
              'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '885051543236593');
            fbq('track', 'PageView');
          `,
        }}
      />
    );

    scripts.push(
      <noscript key="fb-noscript">
        {
          '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=885051543236593&ev=PageView&noscript=1" />'
        }
      </noscript>
    );

    // --------------------------
    // SEARCH ATLAS (OTTO) — REMOVED 2026-07-21
    // Client-side dynamic_optimization.js was redundant: the app already
    // renders titles/meta/canonical/og server-side via react-helmet-async.
    // OTTO only mutated tags after JS ran (invisible to first-pass crawl)
    // and risked overwriting the correct SSR tags. Removed here + from
    // public/index.html + server/csp.js.

    setClientScripts(scripts);
  }, [isBrowser, hasGA4, googleAnalyticsId]);

  // ------------------------------------------------------
  // HELMET SSR-SAFE LOADING (NO window/document HERE)
  // ------------------------------------------------------
  const helmetScripts = [];

  // GA4 loader (SSR-safe)
  if (hasGA4) {
    helmetScripts.push(
      <script
        key="gtag-lib"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
      ></script>
    );
  }

  // MAPBOX SSR-SAFE
  if (isMapboxInUse) {
    helmetScripts.push(
      <script key="mapbox-sdk" src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`} />
    );

    helmetScripts.push(
      <link
        key="mapbox-css"
        rel="stylesheet"
        href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css"
      />
    );

    helmetScripts.push(
      <script
        key="mapbox-lib"
        id={MAPBOX_SCRIPT_ID}
        src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js"
      ></script>
    );
  }

  // GOOGLE MAPS SSR-SAFE
  if (isGoogleMapsInUse) {
    helmetScripts.push(
      <script
        key="google-maps"
        id={GOOGLE_MAPS_SCRIPT_ID}
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
      ></script>
    );
  }

  // JSON-LD (SSR-safe)
  if (schemaArrayJSONString) {
    helmetScripts.push(
      <script
        key="page-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: schemaArrayJSONString.replace(/</g, '\\u003c'),
        }}
      />
    );
  }

  return (
    <>
      <Helmet>{helmetScripts}</Helmet>
      {isBrowser && clientScripts}
    </>
  );
};
